import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SupabaseService } from '../services/supabaseService';
import { BattleRecord } from '../types';
import { TudorChessboard } from '../components/TudorChessboard';
import { Chess } from 'chess.js';
import { ChevronLeft, ChevronRight, Rewind, Play, Pause, ArrowLeft } from 'lucide-react';

const ReplayBattle: React.FC = () => {
    const { battleId } = useParams();
    const navigate = useNavigate();
    const [battle, setBattle] = useState<BattleRecord | null>(null);

    // Playback State
    const [currentMoveIndex, setCurrentMoveIndex] = useState(-1); // -1 = Start
    const [isPlaying, setIsPlaying] = useState(false);

    // Visualization State
    const [boardFen, setBoardFen] = useState("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
    const [startFen, setStartFen] = useState("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");

    // 1. Load Battle Data
    useEffect(() => {
        if (!battleId) return;
        SupabaseService.getBattleById(battleId).then(data => {
            if (data) {
                setBattle(data);

                // Determine Start FEN (Critical for reset/-1 index)
                let initial = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

                // Try Parse PGN for Headers (setup/fen) ONLY to get start position
                const pgnSource = data.result?.pgn || data.pgn;
                if (pgnSource) {
                    const g = new Chess();
                    try {
                        g.loadPgn(pgnSource);
                        const h = g.header();
                        if (h['FEN']) initial = h['FEN'];
                    } catch (e) {
                        console.error("PGN Header Parse Failed", e);
                    }
                }

                setStartFen(initial);
                setBoardFen(initial);
            }
        });
    }, [battleId]);

    // 2. Sync Board to Index
    useEffect(() => {
        if (!battle) return;

        if (currentMoveIndex === -1) {
            setBoardFen(startFen);
            return;
        }

        // Use Rich History Only
        if (battle.battle_history && battle.battle_history.length > 0) {
            // Safety check for index bound
            if (currentMoveIndex >= 0 && currentMoveIndex < battle.battle_history.length) {
                const move = battle.battle_history[currentMoveIndex];
                if (move) setBoardFen(move.fen);
            }
        }
    }, [currentMoveIndex, battle, startFen]);

    const history = battle?.battle_history || [];
    const historyLen = history.length;

    // 3. Auto-Play Logic
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(() => {
            if (currentMoveIndex < historyLen - 1) {
                setCurrentMoveIndex(p => p + 1);
            } else {
                setIsPlaying(false);
            }
        }, 1000); // 1 move per second
        return () => clearInterval(interval);
    }, [isPlaying, currentMoveIndex, historyLen]);

    const handleNext = () => {
        if (currentMoveIndex < historyLen - 1) setCurrentMoveIndex(p => p + 1);
    };

    const handlePrev = () => {
        if (currentMoveIndex > -1) setCurrentMoveIndex(p => p - 1);
    };

    const handleReset = () => {
        setIsPlaying(false);
        setCurrentMoveIndex(-1);
    }

    if (!battle) return <div className="text-center pt-24 text-tudor-gold">Loading Replay...</div>;

    const mySide = battle.settings.side;

    return (
        <div className="flex flex-col lg:flex-row h-screen bg-black text-white font-mono">
            {/* LEFT: Board */}
            <div className="flex-1 flex items-center justify-center bg-zinc-950 p-4 relative pt-24 lg:pt-0">
                <button
                    onClick={() => navigate('/history')}
                    className="absolute top-24 left-4 lg:left-8 flex items-center gap-2 text-zinc-500 hover:text-white transition-colors z-50 bg-black/50 px-3 py-1 rounded-full border border-zinc-800"
                >
                    <ArrowLeft size={16} /> Exit Replay
                </button>
                <div className="w-full max-w-[600px] aspect-square shadow-2xl border border-zinc-800 rounded-lg">
                    <TudorChessboard
                        position={boardFen}
                        arePiecesDraggable={false}
                        boardOrientation={mySide}
                    />
                </div>
            </div>

            {/* RIGHT: Controls */}
            <div className="w-full lg:w-96 bg-zinc-900 border-l border-zinc-800 p-6 flex flex-col pt-24 lg:pt-24 h-1/2 lg:h-full">
                <h2 className="text-2xl font-serif text-tudor-gold tracking-widest mb-6 border-b border-zinc-800 pb-4">
                    Battle Log
                </h2>

                <div className="flex-1 overflow-y-auto mb-6 space-y-1 pr-2">
                    {/* Render moves pairs */}
                    {historyLen === 0 ? (
                        <div className="text-zinc-500 text-sm text-center italic mt-4">No move history available.</div>
                    ) : Array.from({ length: Math.ceil(historyLen / 2) }).map((_, i) => (
                        <div key={i} className={`grid grid-cols-[30px_1fr_1fr] text-sm p-2 rounded ${i === Math.floor(currentMoveIndex / 2) ? 'bg-tudor-gold/10 border border-tudor-gold/30' : ''}`}>
                            <span className="text-zinc-500">{i + 1}.</span>
                            <span className={i * 2 <= currentMoveIndex ? 'text-white' : 'text-zinc-500'}>{history[i * 2]?.san}</span>
                            <span className={(i * 2 + 1) <= currentMoveIndex ? 'text-white' : 'text-zinc-500'}>{history[i * 2 + 1]?.san}</span>
                        </div>
                    ))}
                </div>

                <div className="bg-zinc-950 p-4 rounded-lg border border-zinc-800">
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={handleReset} className="p-2 text-zinc-400 hover:text-white"><Rewind /></button>
                        <button onClick={handlePrev} className="p-2 text-zinc-400 hover:text-white"><ChevronLeft /></button>
                        <button onClick={() => setIsPlaying(!isPlaying)} className="p-4 bg-tudor-gold text-black rounded-full hover:bg-white hover:scale-105 transition-all w-14 h-14 flex items-center justify-center">
                            {isPlaying ? <Pause /> : <Play className="ml-1" />}
                        </button>
                        <button onClick={handleNext} className="p-2 text-zinc-400 hover:text-white"><ChevronRight /></button>
                    </div>
                    <div className="text-center text-xs text-zinc-500 tracking-widest">
                        MOVE {currentMoveIndex + 1} / {historyLen}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ReplayBattle;
